import React, { useState, useEffect } from 'react';
import { formatearMoneda, limpiarMoneda, valorCrudo } from '../../utils/formatMoney';

const API = '/api';

// ── Campo de dinero autónomo (maneja su propio foco, usado dentro de filas de listas) ──
const MoneyField = ({ value, onChange, decimales = 0, style }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <span style={{ position: 'absolute', left: 12, top: 11, color: '#6b7280', fontSize: 14 }}>$</span>
      <input
        type="text"
        inputMode="decimal"
        value={focused ? valorCrudo(value, decimales) : formatearMoneda(value, decimales)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={e => onChange(limpiarMoneda(e.target.value, decimales))}
        style={{ ...style, paddingLeft: 24 }}
        placeholder={decimales === 0 ? '0' : '0.00'}
      />
    </div>
  );
};

// ── Lista repetible genérica (Agregar / Eliminar, hasta `max` filas) ────────────────
const RepeatableList = ({ items, onChange, max, campos, st, isMobile, addLabel = '➕ Agregar' }) => {
  const agregar = () => {
    if (items.length >= max) { alert(`Máximo ${max} registros`); return; }
    const nuevo = {};
    campos.forEach(c => { nuevo[c.name] = ''; });
    onChange([...items, nuevo]);
  };
  const eliminar = idx => onChange(items.filter((_, i) => i !== idx));
  const actualizar = (idx, campo, valor) => onChange(items.map((it, i) => (i === idx ? { ...it, [campo]: valor } : it)));

  const hasAnchoFijo = campos.some(c => c.ancho);
  const gridRow = {
    display: 'grid',
    gridTemplateColumns: isMobile
      ? '1fr'
      : hasAnchoFijo
        ? campos.map(c => `${c.ancho || 200}px`).join(' ')
        : `repeat(${Math.min(campos.length, 4)}, 1fr)`,
    gap: hasAnchoFijo ? 8 : 12,
  };

  return (
    <div>
      {items.map((item, idx) => (
        <div key={idx} style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: 16, marginBottom: 12 }}>
          <div style={gridRow}>
            {campos.map(campo => {
              const val = item[campo.name] ?? '';
              return (
                <div key={campo.name} style={st.field}>
                  <label style={st.label}>{campo.label}</label>
                  {campo.type === 'select' ? (
                    <select value={val} onChange={e => actualizar(idx, campo.name, e.target.value)} style={st.input}>
                      <option value="">-- Seleccionar --</option>
                      {campo.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : campo.type === 'money' ? (
                    <MoneyField value={val} decimales={campo.decimales || 0} onChange={v => actualizar(idx, campo.name, v)} style={st.inputNum} />
                  ) : campo.type === 'date' ? (
                    <input type="date" value={val} onChange={e => actualizar(idx, campo.name, e.target.value)} style={st.input} />
                  ) : campo.type === 'siNo' ? (
                    <select value={val === true || val === 'true' ? 'true' : val === false || val === 'false' ? 'false' : ''}
                      onChange={e => actualizar(idx, campo.name, e.target.value === '' ? '' : e.target.value === 'true')} style={st.input}>
                      <option value="">-- Seleccionar --</option>
                      <option value="true">Sí</option>
                      <option value="false">No</option>
                    </select>
                  ) : (
                    <input type="text" value={val} onChange={e => actualizar(idx, campo.name, e.target.value)} style={st.input} maxLength={campo.maxLength} />
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ textAlign: 'right', marginTop: 4 }}>
            <button type="button" onClick={() => eliminar(idx)}
              style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 13, cursor: 'pointer', padding: 4 }}>
              🗑️ Eliminar
            </button>
          </div>
        </div>
      ))}
      <button type="button" onClick={agregar} disabled={items.length >= max}
        style={{
          backgroundColor: items.length >= max ? '#e5e7eb' : '#1a3a6e', color: items.length >= max ? '#9ca3af' : 'white',
          border: 'none', padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: '600',
          cursor: items.length >= max ? 'not-allowed' : 'pointer',
        }}>
        {addLabel} ({items.length}/{max})
      </button>
    </div>
  );
};

const CuestionarioCualitativo = ({ sn, upd, st, isMobile, participante, titulo, esAsalariado, estadosInmueble }) => {
  const p = participante; // 'titular' | 'coac' — prefijo de campos, igual convención que el resto del módulo
  const campo = suf => `${p}_${suf}`;
  const val = suf => sn[campo(suf)];
  const list = suf => sn[campo(suf)] || [];
  const setList = (suf, items) => upd(campo(suf), items);

  const [municipios, setMunicipios] = useState([]);

  useEffect(() => {
    const estado = val('gar_estado');
    if (!estado) { setMunicipios([]); return; }
    fetch(`${API}/sepomex/municipios/${encodeURIComponent(estado)}`)
      .then(r => r.json())
      .then(d => setMunicipios(d.map(m => m.municipio || m)))
      .catch(e => console.error('Error cargando municipios:', e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sn[campo('gar_estado')]]);

  const renderSiNo = suf => (
    <select value={val(suf) === true ? 'true' : val(suf) === false ? 'false' : ''}
      onChange={e => upd(campo(suf), e.target.value === '' ? null : e.target.value === 'true')} style={st.input}>
      <option value="">-- Seleccionar --</option>
      <option value="true">Sí</option>
      <option value="false">No</option>
    </select>
  );

  const renderText = (suf, label, opts = {}) => (
    <div style={st.field}>
      <label style={st.label}>{label}</label>
      <input type="text" value={val(suf) || ''} onChange={e => upd(campo(suf), e.target.value)} style={st.input} maxLength={opts.maxLength} />
    </div>
  );

  const renderTextarea = (suf, label) => (
    <div style={st.field}>
      <label style={st.label}>{label}</label>
      <textarea value={val(suf) || ''} onChange={e => upd(campo(suf), e.target.value)}
        style={{ ...st.input, height: 70, paddingTop: 8, resize: 'vertical', fontFamily: 'Helvetica, Arial, sans-serif' }} />
    </div>
  );

  const renderNumero = (suf, label) => (
    <div style={st.field}>
      <label style={st.label}>{label}</label>
      <input type="number" value={val(suf) || ''} onChange={e => upd(campo(suf), e.target.value)} style={st.inputNum} />
    </div>
  );

  const renderCheckbox = (suf, label) => (
    <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: 8 }}>
      <input type="checkbox" checked={val(suf) === true} onChange={e => upd(campo(suf), e.target.checked)} />
      {label}
    </label>
  );

  return (
    <div style={st.card}>
      <h3 style={st.title}>{titulo}</h3>

      {/* DEPENDIENTES ECONÓMICOS */}
      <h4 style={st.subtitle}>👨‍👩‍👧 Dependientes Económicos</h4>
      <RepeatableList
        items={list('dependientes')} onChange={items => setList('dependientes', items)} max={5} st={st} isMobile={isMobile}
        campos={[
          { name: 'nombre', label: 'Nombre', ancho: 470 },
          { name: 'rfc', label: 'RFC', maxLength: 13, ancho: 168 },
          { name: 'fecha_nacimiento', label: 'Fecha Nacimiento', type: 'date', ancho: 196 },
          { name: 'numero_cliente', label: 'Número de Cliente', ancho: 140 },
        ]}
      />

      {/* 1. GARANTÍA HIPOTECARIA */}
      <h4 style={st.subtitle}>🏠 1. Garantía Hipotecaria (Inmueble a Adquirir)</h4>
      <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
        Si no se tiene el domicilio exacto indicar Colonia, Municipio y Estado
      </p>

      <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>1.1 Ubicación Exacta</p>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
        {renderText('gar_calle_numero', 'Calle y Número')}
        {renderText('gar_colonia', 'Colonia')}
        <div style={st.field}>
          <label style={st.label}>Estado</label>
          <select value={val('gar_estado') || ''} onChange={e => { upd(campo('gar_estado'), e.target.value); upd(campo('gar_municipio'), ''); }} style={st.input}>
            <option value="">-- Seleccionar --</option>
            {(estadosInmueble || []).map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
        <div style={st.field}>
          <label style={st.label}>Municipio</label>
          <select value={val('gar_municipio') || ''} onChange={e => upd(campo('gar_municipio'), e.target.value)} style={st.input}>
            <option value="">-- Seleccionar --</option>
            {municipios.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>

      <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>1.2 Nombre completo de Vendedor y Parentesco</p>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 16, marginBottom: 16 }}>
        {renderText('gar_vendedor_nombre', 'Nombre')}
        {renderText('gar_vendedor_parentesco', 'Parentesco')}
      </div>

      <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>1.3 Propiedades Inmobiliarias</p>
      <RepeatableList
        items={list('propiedades')} onChange={items => setList('propiedades', items)} max={5} st={st} isMobile={isMobile}
        campos={[
          { name: 'tipo_inmueble', label: 'Tipo Inmueble', type: 'select', ancho: 125, options: [{ value: 'Casa', label: 'Casa' }, { value: 'Departamento', label: 'Departamento' }, { value: 'Terreno', label: 'Terreno' }] },
          { name: 'caracteristicas', label: 'Características (Ubicación, m2, etc.)', ancho: 570 },
          { name: 'valor_estimado', label: 'Valor estimado', type: 'money', ancho: 125 },
        ]}
      />

      {/* 2. VINCULACIÓN CON BANCO SANTANDER */}
      <h4 style={st.subtitle}>🏦 2. Vinculación con Banco Santander</h4>

      <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
        2.1 Posición actual con Santander (Créditos y/o Inversiones) de los participantes del crédito
      </p>
      <RepeatableList
        items={list('vinc_creditos')} onChange={items => setList('vinc_creditos', items)} max={3} st={st} isMobile={isMobile}
        campos={[
          { name: 'producto', label: 'Producto' },
          { name: 'saldo', label: 'Saldo', type: 'money' },
          { name: 'tipo_participante', label: 'Tipo de Participante', type: 'select', options: [{ value: 'Titular', label: 'Titular' }, { value: 'Coacreditado', label: 'Coacreditado' }] },
        ]}
      />

      <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginTop: 16, marginBottom: 8 }}>
        2.2 Posición actual con Santander de las Empresas con participación accionaria o control
      </p>
      <RepeatableList
        items={list('vinc_empresas')} onChange={items => setList('vinc_empresas', items)} max={3} st={st} isMobile={isMobile}
        campos={[
          { name: 'producto', label: 'Producto' },
          { name: 'nombre_empresa', label: 'Nombre de la Empresa' },
          { name: 'rfc', label: 'RFC', maxLength: 13 },
          { name: 'saldo', label: 'Saldo', type: 'money' },
          { name: 'comprueba_ingresos', label: '¿Comprobará ingresos con la empresa?', type: 'siNo' },
        ]}
      />

      <div style={{ marginTop: 16, marginBottom: 8 }}>
        <label style={st.label}>2.3 ¿Alguno de los participantes participa como socio accionista de empresa con relación de negocio con Santander?</label>
        {renderSiNo('vinc_socio_accionista')}
      </div>
      {val('vinc_socio_accionista') === true && (
        <RepeatableList
          items={list('vinc_socios')} onChange={items => setList('vinc_socios', items)} max={3} st={st} isMobile={isMobile}
          campos={[
            { name: 'nombre_empresa', label: 'Nombre de la Empresa' },
            { name: 'rfc', label: 'RFC', maxLength: 13 },
            { name: 'porcentaje_participacion', label: '% Participación Accionaria', type: 'number' },
          ]}
        />
      )}

      <div style={{ marginTop: 16, maxWidth: isMobile ? '100%' : '50%' }}>
        {renderText('vinc_ejecutivo_nombre', '2.4 ¿Cuenta con un ejecutivo de Banca de Empresas o Banca Privada? — Nombre del ejecutivo')}
      </div>

      <div style={{ marginTop: 16, marginBottom: 8, maxWidth: isMobile ? '100%' : '33%' }}>
        <label style={st.label}>2.5 ¿Existe plan de negocios futuro con Banco Santander?</label>
        {renderSiNo('vinc_existe_plan_negocios')}
      </div>
      {val('vinc_existe_plan_negocios') === true && (
        <RepeatableList
          items={list('plan_negocios')} onChange={items => setList('plan_negocios', items)} max={3} st={st} isMobile={isMobile}
          campos={[
            { name: 'producto', label: 'Producto' },
            { name: 'potencial_negocio', label: 'Potencial de Negocio' },
          ]}
        />
      )}

      <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginTop: 16, marginBottom: 8 }}>
        2.7 En caso de conocer o ser referido de algún funcionario de Banco Santander
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 16, marginBottom: 8 }}>
        {renderText('vinc_funcionario_nombre', 'Nombre completo')}
        {renderText('vinc_funcionario_area', 'Área')}
        {renderText('vinc_funcionario_puesto', 'Puesto')}
      </div>

      {/* Solo para asalariados: si además es Profesionista Independiente, se habilitan las secciones 3 y 4 */}
      {esAsalariado && (
        <div style={{ marginTop: 16, marginBottom: 8, maxWidth: isMobile ? '100%' : '33%' }}>
          <label style={st.label}>2.8 ¿Es PFAE también?</label>
          {renderSiNo('es_profesionista_independiente')}
        </div>
      )}

      {/* SECCIONES DE ACTIVIDAD ECONÓMICA Y DATOS PATRIMONIALES: para no asalariados siempre,
          y para asalariados solo si declaran ser Profesionista Independiente */}
      {(!esAsalariado || val('es_profesionista_independiente') === true) && (
        <>
          <h4 style={st.subtitle}>📊 3. Actividad Económica</h4>
          {renderTextarea('act_descripcion', '3.1 Describir detalladamente sus Actividades y/o las de la Empresa')}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 16 }}>
            {renderNumero('act_continuidad_anios', '3.2 Continuidad (en años) en el Giro/Actividad')}
            <div style={st.field}>
              <label style={st.label}>3.4 ¿La Administración del negocio es familiar?</label>
              {renderSiNo('act_admin_familiar')}
            </div>
          </div>
          {renderTextarea('act_ciudades_presencia', '3.3 Indique las ciudades donde tiene presencia')}

          <div style={{ marginBottom: 12 }}>
            <label style={st.label}>3.5 Nivel de Comercialización de su Producto/Servicio</label>
            {renderCheckbox('act_comerc_venta_directa', 'Venta directa al público')}
            {renderCheckbox('act_comerc_por_contratos', 'Por contratos')}
            {val('act_comerc_por_contratos') === true && (
              <p style={{ fontSize: 12, color: '#e11d48', marginLeft: 24, marginBottom: 8 }}>Se anexarán soportes</p>
            )}
            {renderCheckbox('act_comerc_mayoreo', 'Al mayoreo')}
          </div>

          {renderNumero('act_porcentaje_participacion', '3.6 En caso de ser accionista de la Empresa, indique porcentaje de participación (%)')}

          <div style={st.field}>
            <label style={st.label}>3.7 ¿Existe Variabilidad en sus Ventas?</label>
            {renderSiNo('act_variabilidad_ventas')}
          </div>
          {val('act_variabilidad_ventas') === true && (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 16 }}>
              {renderTextarea('act_variabilidad_por_que', '¿Por qué?')}
              {renderTextarea('act_variabilidad_meses_fuertes', '¿Cuáles son los meses fuertes?')}
            </div>
          )}

          {renderTextarea('act_marcas_productos', '3.8 Principales marcas o productos que maneja el negocio')}
          {renderTextarea('act_principales_clientes', '3.9 Principales clientes')}
          {renderTextarea('act_principales_proveedores', '3.10 Principales proveedores')}

          <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginTop: 12, marginBottom: 8 }}>3.11 Otros negocios</p>
          <RepeatableList
            items={list('otros_negocios')} onChange={items => setList('otros_negocios', items)} max={2} st={st} isMobile={isMobile}
            campos={[
              { name: 'razon_social', label: 'Razón Social' },
              { name: 'giro', label: 'Giro' },
              { name: 'porcentaje_participacion', label: 'Porcentaje de participación', type: 'text' },
            ]}
          />

          <h4 style={st.subtitle}>💎 4. Datos Patrimoniales de los Declarantes</h4>
          <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>4.2 Valores y otros bienes muebles</p>
          <RepeatableList
            items={list('valores_bienes')} onChange={items => setList('valores_bienes', items)} max={5} st={st} isMobile={isMobile}
            campos={[
              { name: 'tipo_patrimonio', label: 'Tipo de Patrimonio y características' },
              { name: 'importe', label: 'Importe', type: 'money' },
            ]}
          />
        </>
      )}
    </div>
  );
};

export default CuestionarioCualitativo;
